import { useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import parse from 'html-react-parser';
import cx from 'classnames';

import ChordBarsChart from '../../ChordBarsChart';
import PartAssignments from '../../PartAssignments';
import IntensityMeter from '../../IntensityMeter';
import CountedView from './CountedView';

import useFontSize from '../../../hooks/useFontSize';
import useSoundingChord from '../../../hooks/useSoundingChord';
import theme from '../../../utils/theme';
import { getBeatsPerBar } from '../../../utils/songTiming';

const NO_CHORD = -1;

/**
 * One part of the run: its chip and any notes riding with it, then its words where the chart can be
 * followed through them, or its chart where it can't — an instrumental, or words that don't carry
 * the chart's chords. A part with no chart, or a note, shows just its words and is never lit.
 *
 * @param {object} segment - The pass of this part playing now, or null while another part plays
 * @param {object} scroller - Ref to the run's box, which scrolls rather than the dialog
 */
const RunPart = ({
  entry,
  isFirst,
  segment,
  scroller,
  currentBar,
  currentChord,
  currentChordSeconds,
  beatsPerBar,
  showIntensity,
}) => {
  const active = Boolean(segment);
  const content = useMemo(() => (entry.lyricsHtml ? parse(entry.lyricsHtml) : null), [entry.lyricsHtml]);
  const notes = useMemo(() => (entry.notesHtml ? parse(entry.notesHtml) : null), [entry.notesHtml]);
  // Every part runs its own, lit only while it is the one playing: the nth chord of a part's chart
  // is the nth chord over its own words, not the song's
  const lyricsRef = useSoundingChord(active ? currentChord : NO_CHORD, active ? currentChordSeconds : 0, content, {
    contain: scroller,
  });
  // The size the reader set the page to, as in the single part's Lyrics view
  const fontSize = useFontSize();
  const headRef = useRef(null);

  // A part coming in, or going round again, brings its top to the reader — a grid has no lit chord
  // to scroll to, and words starting over are back at the top of the part. Inside the run's box
  // only: scrolling the dialog would take the controls under it away.
  useEffect(() => {
    const box = scroller.current;
    const head = headRef.current;

    if (!active || !box || !head) return;

    box.scrollTop += head.getBoundingClientRect().top - box.getBoundingClientRect().top;
  }, [active, segment?.pass, scroller]);

  return (
    <section className={cx('run-part', { 'run-part--playing': active })}>
      <div className="run-head" ref={headRef}>
        <span className={theme.renderPartColor(entry.name, 'chip')}>{entry.title}</span>
        {showIntensity && entry.lines && (
          <IntensityMeter level={segment?.intensity ?? entry.intensity} className="run-intensity" />
        )}
        <PartAssignments name={entry.name} people={entry.people} instruments={entry.instruments} />
      </div>
      {notes && (
        <div className="run-notes song-chart" style={{ fontSize }}>
          {notes}
        </div>
      )}
      {content && (
        <div className="lyrics song-chart" style={{ fontSize }} ref={lyricsRef}>
          {content}
        </div>
      )}
      {!content && entry.lines && (
        <ChordBarsChart
          lines={entry.lines}
          sections={entry.sections}
          repeatCount={entry.shownRepeatCount}
          beatsPerBar={beatsPerBar}
          // Only the part played first comes in over the count-in; a later one's opening rest is silence
          countInEntry={isFirst && entry.countInEntry}
          currentBar={active ? currentBar : -1}
          currentChord={active ? currentChord : NO_CHORD}
          currentChordSeconds={active ? currentChordSeconds : 0}
          spellChord={entry.spellChord}
        />
      )}
    </section>
  );
};

RunPart.propTypes = {
  entry: PropTypes.object.isRequired,
  isFirst: PropTypes.bool,
  segment: PropTypes.object,
  scroller: PropTypes.object.isRequired,
  currentBar: PropTypes.number.isRequired,
  currentChord: PropTypes.number.isRequired,
  currentChordSeconds: PropTypes.number.isRequired,
  beatsPerBar: PropTypes.number.isRequired,
  showIntensity: PropTypes.bool,
};

/**
 * The rest of the song from the part that was opened, one part after another in one scrolling
 * box — what the player shows in place of a single part while it is playing through.
 *
 * @param {object[]} entries - The parts that play, from ChartRunContext
 * @param {object[]} shown - What the run lists: `entries`, or in a fully charted song every part
 *   with notes and section titles among them — see getRunFrom
 * @param {object[]} segments - The run's, from buildChartRun: which part plays when, and how often
 * @param {number} currentSegment - The segment playing, -1 for none
 * @param {boolean} showIntensity - Each part's meter; left off where intensity is off or unset
 */
const ChartRun = ({
  entries,
  shown,
  segments,
  currentSegment,
  currentBar,
  currentChord,
  currentChordSeconds,
  countInBeat,
  beat,
  timeSignature,
  showIntensity = true,
}) => {
  const playing = segments[currentSegment] || null;
  const beatsPerBar = getBeatsPerBar(timeSignature);
  const runRef = useRef(null);

  return (
    <CountedView countInBeat={countInBeat} beat={beat} timeSignature={timeSignature} entered={currentChord >= 0}>
      <div className="run" ref={runRef}>
        {shown.map((entry) => {
          if (entry.section) {
            return (
              <h2 key={entry.partId} className="run-section">
                {entry.title}
              </h2>
            );
          }

          const index = entries.indexOf(entry);

          // A chart the player couldn't play is left out of the run, and so out of here
          if (index >= 0 && !segments.some((segment) => segment.entryIndex === index)) return null;

          return (
            <RunPart
              key={entry.partId}
              entry={entry}
              isFirst={index >= 0 && index === segments[0]?.entryIndex}
              segment={index >= 0 && playing?.entryIndex === index ? playing : null}
              scroller={runRef}
              currentBar={currentBar}
              currentChord={currentChord}
              currentChordSeconds={currentChordSeconds}
              beatsPerBar={beatsPerBar}
              showIntensity={showIntensity}
            />
          );
        })}
      </div>
    </CountedView>
  );
};

ChartRun.propTypes = {
  entries: PropTypes.arrayOf(PropTypes.object).isRequired,
  shown: PropTypes.arrayOf(PropTypes.object).isRequired,
  segments: PropTypes.arrayOf(PropTypes.object).isRequired,
  currentSegment: PropTypes.number.isRequired,
  currentBar: PropTypes.number.isRequired,
  currentChord: PropTypes.number.isRequired,
  currentChordSeconds: PropTypes.number.isRequired,
  countInBeat: PropTypes.number.isRequired,
  beat: PropTypes.number.isRequired,
  timeSignature: PropTypes.string.isRequired,
  showIntensity: PropTypes.bool,
};

export default ChartRun;
